import { useState } from 'react';
import {
  ArrowRight, Check, Clock, MapPin, Home as HomeIcon,
  ShieldCheck, Wrench, ChevronDown, Star, Crown, Trophy,
  Sparkles, Handshake, Award, Car,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import SectionLabel from '../components/SectionLabel';
import BookingCTA from '../components/BookingCTA';
import SEO from '../components/SEO';
import { faqSchema, mobileDetailingSchema } from '../lib/schema';
import { useLang } from '../context/LanguageContext';

const VOORDEEL_ICONS = [Sparkles, Handshake, Award, ShieldCheck];
const SUB_ICONS = { essential: Star, premium: Crown, exclusive: Trophy };

const DetailingAanHuis = () => {
  const [openFaq, setOpenFaq] = useState(0);
  const { c, ui, lp } = useLang();
  const M = c.mobile;
  const t = ui.mobile;

  return (
    <div className="bg-[#0a0a0b] text-white">
      <SEO
        path="/detailing-aan-huis"
        title="Detailing aan huis Deventer, Apeldoorn, Zutphen & Raalte"
        description="Auto detailing aan huis in Deventer en omgeving. Professioneel auto poetsen, mobiele autodetailing en interieurreiniging op locatie — binnen 20 km van Deventer gratis voorrijden. Vraag vrijblijvend een offerte aan."
        keywords="detailing aan huis, auto poetsen aan huis, mobiele autodetailing, auto reinigen op locatie, interieurreiniging aan huis, detailing aan huis Deventer, Apeldoorn, Zutphen, Raalte"
        jsonLd={[
          mobileDetailingSchema({ name: t.heroTitle.join(' '), description: t.heroSubtitle }),
          faqSchema(M.faq.map(f => ({ q: f.q, a: f.a }))),
        ]}
      />
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-end pb-20 pt-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1607860108855-64acf2078ed9?w=1920&q=85" alt={t.heroImgAlt} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0b] via-[#0a0a0b]/85 to-[#0a0a0b]/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0b] via-transparent to-transparent" />
        </div>
        <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 w-full">
          <SectionLabel>{t.heroLabel}</SectionLabel>
          <h1 className="mt-6 text-5xl md:text-7xl lg:text-[80px] font-medium leading-[1.05] tracking-tight max-w-5xl">
            {t.heroTitle[0]}<br />{t.heroTitle[1]}
          </h1>
          <p className="mt-8 text-white/75 text-lg md:text-xl max-w-2xl leading-relaxed">
            {t.heroSubtitle}
          </p>
          <p className="mt-4 text-white/45 text-sm md:text-base max-w-2xl italic">
            {t.heroItalic}
          </p>
          <div className="mt-10">
            <BookingCTA size="lg" />
          </div>
        </div>
      </section>

      {/* WAAROM */}
      <section className="py-24 lg:py-32 border-t border-white/5">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <SectionLabel><span className="mx-auto">{t.waaromLabel}</span></SectionLabel>
            <h2 className="mt-5 text-4xl md:text-5xl font-medium tracking-tight leading-[1.05]">{t.waaromTitle[0]}<br />{t.waaromTitle[1]}</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {M.voordelen.map((v, i) => {
              const Icon = VOORDEEL_ICONS[i] || Sparkles;
              return (
                <div key={i} className="group p-7 rounded-xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/20 transition-all">
                  <span className="w-11 h-11 rounded-lg bg-[#2f6bff]/15 text-[#2f6bff] flex items-center justify-center mb-5 group-hover:bg-[#2f6bff]/25 transition-colors">
                    <Icon className="w-5 h-5" />
                  </span>
                  <h3 className="text-white text-lg font-medium leading-tight">{v.title}</h3>
                  <p className="mt-2 text-white/55 text-sm leading-relaxed">{v.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WERKWIJZE */}
      <section className="py-24 lg:py-32 border-t border-white/5 bg-white/[0.015]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <SectionLabel><span className="mx-auto">{t.werkwijzeLabel}</span></SectionLabel>
            <h2 className="mt-5 text-4xl md:text-5xl font-medium tracking-tight leading-[1.05]">{t.werkwijzeTitle[0]}<br />{t.werkwijzeTitle[1]}</h2>
            <p className="mt-4 text-white/55 text-sm">{t.werkwijzeSub}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {M.stappen.map((s, i) => (
              <div key={s.n} className="relative p-7 rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] transition-colors">
                <div className="text-[#2f6bff] text-sm font-medium tracking-[0.25em]">{s.n}</div>
                <h3 className="mt-4 text-xl font-medium leading-tight">{s.title}</h3>
                <p className="mt-3 text-white/60 text-sm leading-relaxed">{s.text}</p>
                {i < M.stappen.length - 1 && <div className="hidden lg:block absolute right-[-14px] top-1/2 -translate-y-1/2 text-white/20"><ArrowRight className="w-6 h-6" /></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LOSSE BEHANDELINGEN */}
      <section className="py-24 lg:py-32 border-t border-white/5">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-6">
            <SectionLabel><span className="mx-auto">{t.losseLabel}</span></SectionLabel>
            <h2 className="mt-5 text-4xl md:text-5xl font-medium tracking-tight leading-[1.05]">
              {t.losseTitle[0]}<br />{t.losseTitle[1]}
            </h2>
            <p className="mt-5 text-white/60 max-w-2xl mx-auto">
              {t.losseText}
            </p>
          </div>

          {/* Mobiel */}
          <div className="mt-16">
            <div className="flex items-center gap-3 mb-8">
              <span className="w-10 h-10 rounded-lg bg-[#2f6bff]/15 text-[#2f6bff] flex items-center justify-center"><HomeIcon className="w-5 h-5" /></span>
              <div>
                <div className="text-white/50 text-xs tracking-[0.25em] uppercase">{t.atHomeEyebrow}</div>
                <h3 className="text-white text-2xl font-medium">{t.atHomeTitle}</h3>
              </div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {M.mobileTreatments.map(item => (
                <article key={item.id} className="p-8 rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/20 transition-all flex flex-col">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="flex flex-wrap gap-2">
                        <span className="inline-block px-2.5 py-1 rounded-md bg-[#2f6bff]/15 text-[#7aa3ff] text-[10px] tracking-[0.2em] uppercase">{t.atHomeBadge}</span>
                        {item.badge && <span className="inline-block px-2.5 py-1 rounded-md bg-[#2f6bff] text-white text-[10px] tracking-[0.2em] uppercase font-medium">{item.badge}</span>}
                      </div>
                      <h4 className="mt-3 text-2xl font-medium">{item.name}</h4>
                      <div className="mt-1 text-white/50 text-xs flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {item.duration}</div>
                    </div>
                    <div className="text-right"><div className="text-3xl font-medium">{item.price}</div></div>
                  </div>
                  <p className="mt-4 text-white/65 text-sm">{item.desc}</p>
                  <ul className="mt-5 grid sm:grid-cols-2 gap-x-4 gap-y-2 flex-1">
                    {item.items.map(i => (
                      <li key={i} className="flex items-start gap-2 text-white/85 text-sm">
                        <span className="mt-1 w-4 h-4 rounded-full bg-[#2f6bff]/20 text-[#2f6bff] flex items-center justify-center flex-shrink-0"><Check className="w-2.5 h-2.5" /></span>
                        {i}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-7">
                    <BookingCTA waMessage={item.wa} offerteTreatment={item.name} />
                  </div>
                </article>
              ))}
            </div>
            <p data-testid="mobile-price-note" className="mt-6 text-white/55 text-sm leading-relaxed max-w-3xl">
              {t.priceNote}
            </p>
          </div>

          {/* Premium op locatie */}
          <div className="mt-24">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-10 h-10 rounded-lg bg-[#2f6bff]/15 text-[#2f6bff] flex items-center justify-center"><Wrench className="w-5 h-5" /></span>
              <div>
                <div className="text-white/50 text-xs tracking-[0.25em] uppercase">{t.premiumEyebrow}</div>
                <h3 className="text-white text-2xl font-medium">{t.premiumTitle}</h3>
              </div>
            </div>
            <p className="text-white/60 max-w-3xl mb-10">
              {t.premiumText}
            </p>

            <div className="grid lg:grid-cols-2 gap-5">
              {M.premiumTreatments.map(item => (
                <article key={item.id} className="relative p-8 rounded-2xl border overflow-hidden flex flex-col transition-all border-white/10 bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/20">
                  {item.badge && <span className="absolute top-5 right-5 px-3 py-1 rounded-md bg-[#2f6bff] text-white text-[10px] tracking-[0.2em] uppercase font-medium">{item.badge}</span>}
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/5 text-white/70 text-[10px] tracking-[0.2em] uppercase"><MapPin className="w-3 h-3" /> {t.onlyLocation}</span>
                      <h4 className="mt-3 text-2xl md:text-3xl font-medium leading-tight">{item.name}</h4>
                      <div className="mt-1 text-white/50 text-xs flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {item.duration}</div>
                    </div>
                    <div className="text-right"><div className="text-3xl md:text-4xl font-medium">{item.price}</div></div>
                  </div>
                  <p className="mt-4 text-white/65 text-sm">{item.desc}</p>
                  <ul className="mt-5 grid sm:grid-cols-2 gap-x-4 gap-y-2 flex-1">
                    {item.items.map(i => (
                      <li key={i} className="flex items-start gap-2 text-white/85 text-sm">
                        <span className="mt-1 w-4 h-4 rounded-full bg-[#2f6bff]/20 text-[#2f6bff] flex items-center justify-center flex-shrink-0"><Check className="w-2.5 h-2.5" /></span>
                        {i}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-7">
                    <BookingCTA waMessage={item.wa} offerteTreatment={item.name} />
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SUBSCRIPTIONS */}
      <section className="py-24 lg:py-32 border-t border-white/5 bg-white/[0.015]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <SectionLabel><span className="mx-auto">{t.subsLabel}</span></SectionLabel>
            <h2 className="mt-5 text-4xl md:text-5xl font-medium tracking-tight leading-[1.05]">{t.subsTitle}</h2>
            <p className="mt-5 text-white/60">
              {t.subsText}
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-5 items-stretch">
            {M.subscriptions.map(s => {
              const Icon = SUB_ICONS[s.id] || Star;
              return (
                <article key={s.id} className={`relative rounded-2xl border overflow-hidden flex flex-col p-8 transition-all ${
                  s.premium
                    ? 'border-[#d4af37]/50 bg-gradient-to-br from-[#d4af37]/8 via-white/[0.03] to-transparent lg:scale-[1.04] lg:shadow-[0_20px_60px_-20px_rgba(212,175,55,0.3)]'
                    : 'border-white/10 bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/20'
                }`}>
                  {s.badge && (
                    <span className={`absolute top-5 right-5 px-3 py-1 rounded-md text-[10px] tracking-[0.2em] uppercase font-medium ${
                      s.premium ? 'bg-[#d4af37] text-black' : 'bg-[#2f6bff] text-white'
                    }`}>{s.badge}</span>
                  )}
                  <div className="flex items-center gap-3 mb-1">
                    <span className={`w-11 h-11 rounded-lg flex items-center justify-center ${
                      s.premium ? 'bg-[#d4af37]/15 text-[#d4af37]' : 'bg-[#2f6bff]/15 text-[#2f6bff]'
                    }`}><Icon className="w-5 h-5" /></span>
                    <h3 className="text-2xl font-medium">{s.name}</h3>
                  </div>
                  <div className="mt-6">
                    <div className="flex items-baseline gap-2">
                      <span className="text-white/40 text-sm">{t.vanaf}</span>
                      <span className="text-5xl font-medium tracking-tight">{s.price}</span>
                    </div>
                    <div className="text-white/50 text-sm mt-1">{s.period}</div>
                    {s.freq && <div className={`mt-3 inline-block px-2.5 py-1 rounded-md text-[11px] tracking-[0.15em] uppercase ${
                      s.premium ? 'bg-[#d4af37]/15 text-[#e8c765]' : 'bg-[#2f6bff]/15 text-[#7aa3ff]'
                    }`}>{s.freq}</div>}
                  </div>
                  <p className="mt-5 text-white/70 text-sm leading-relaxed">{s.desc}</p>
                  <ul className="mt-6 space-y-2 flex-1">
                    {s.items.map(i => (
                      <li key={i} className="flex items-start gap-2.5 text-white/85 text-sm">
                        <span className={`mt-1 w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
                          s.premium ? 'bg-[#d4af37]/20 text-[#d4af37]' : 'bg-[#2f6bff]/20 text-[#2f6bff]'
                        }`}><Check className="w-2.5 h-2.5" /></span>
                        {i}
                      </li>
                    ))}
                  </ul>
                  {s.extra && (
                    <div className={`mt-5 p-3 rounded-lg text-xs ${
                      s.premium ? 'bg-[#d4af37]/10 text-[#e8c765] border border-[#d4af37]/20' : 'bg-[#2f6bff]/10 text-[#7aa3ff] border border-[#2f6bff]/20'
                    }`}>
                      <span className="font-medium">{t.extra}</span> {s.extra}
                    </div>
                  )}
                  <div className="mt-6">
                    <BookingCTA waMessage={s.wa} offerteTreatment={s.name} />
                  </div>
                </article>
              );
            })}
          </div>

          {/* GROTE AUTO'S */}
          <div className="mt-6 p-8 md:p-10 rounded-2xl border border-white/10 bg-white/[0.02]">
            <div className="flex items-center gap-2 text-[#2f6bff] text-xs tracking-[0.25em] uppercase">
              <Car className="w-4 h-4" /> {t.bigEyebrow}
            </div>
            <h3 className="mt-3 text-2xl md:text-3xl font-medium tracking-tight leading-tight">
              {t.bigTitle}
            </h3>
            <p className="mt-4 text-white/65 text-sm max-w-3xl leading-relaxed">
              {t.bigText}
            </p>
            <div className="mt-6 grid sm:grid-cols-3 gap-4">
              {M.bigCars.map(b => (
                <div key={b.id} className={`p-5 rounded-lg border ${b.id === 'premium' ? 'border-[#d4af37]/30 bg-[#d4af37]/[0.05]' : 'border-white/10 bg-white/[0.02]'}`}>
                  <div className={`text-[11px] tracking-[0.2em] uppercase ${b.id === 'premium' ? 'text-[#e8c765]' : 'text-white/50'}`}>{b.name}</div>
                  <div className="mt-2 text-white text-2xl font-medium">{b.price} <span className="text-white/50 text-sm font-normal">{t.pm}</span></div>
                </div>
              ))}
            </div>
            <p className="mt-5 text-white/45 text-sm italic">
              {t.bigExamples}
            </p>
          </div>

          <div className="mt-10 p-8 md:p-10 rounded-2xl border border-white/10 bg-white/[0.02]">
            <div className="flex items-center gap-2 text-[#2f6bff] text-xs tracking-[0.25em] uppercase">
              <ShieldCheck className="w-4 h-4" /> {t.flexEyebrow}
            </div>
            <h3 className="mt-3 text-2xl md:text-3xl font-medium tracking-tight leading-tight">
              {t.flexTitle}
            </h3>
            <p className="mt-4 text-white/65 text-sm max-w-3xl leading-relaxed">
              {t.flexText}
            </p>
            <div className="mt-6 grid md:grid-cols-2 gap-4">
              <div className="p-5 rounded-lg border border-white/10 bg-white/[0.02]">
                <div className="text-white/50 text-[11px] tracking-[0.2em] uppercase">{t.particulieren}</div>
                <div className="mt-2 text-white text-lg font-medium">{t.partTerm}</div>
                <div className="text-white/55 text-sm mt-1">{t.partSub}</div>
              </div>
              <div className="p-5 rounded-lg border border-white/10 bg-white/[0.02]">
                <div className="text-white/50 text-[11px] tracking-[0.2em] uppercase">{t.zakelijk}</div>
                <div className="mt-2 text-white text-lg font-medium">{t.zakTerm}</div>
                <div className="text-white/55 text-sm mt-1">{t.zakSub}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MOBIELE TEKST BLOK */}
      <section className="py-24 border-t border-white/5">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 grid md:grid-cols-2 gap-6">
          <div className="p-8 rounded-2xl border border-white/10 bg-white/[0.02]">
            <div className="flex items-center gap-2 text-[#2f6bff] text-xs tracking-[0.25em] uppercase">
              <MapPin className="w-4 h-4" /> {t.homeBlockEyebrow}
            </div>
            <h3 className="mt-3 text-2xl font-medium tracking-tight">{t.homeBlockTitle}</h3>
            <p className="mt-4 text-white/70 text-sm leading-relaxed">
              {t.homeBlockText}
            </p>
            <ul className="mt-5 space-y-2 text-white/80 text-sm">
              {t.homeBullets.map((b, i) => (
                <li key={i} className="flex gap-2"><span className="text-[#2f6bff]">•</span> <span>{b[0]}{b[1] && <span className="text-white">{b[1]}</span>}{b[2]}</span></li>
              ))}
            </ul>
            <p className="mt-4 text-white/55 text-sm italic">{t.homeItalic}</p>
            <Link to={lp('apeldoorn')} className="mt-3 inline-block text-[#7aa3ff] hover:text-white text-sm transition-colors">{t.apeldoornLink}</Link>
          </div>
          <div className="p-8 rounded-2xl border border-white/10 bg-white/[0.02]">
            <div className="flex items-center gap-2 text-[#2f6bff] text-xs tracking-[0.25em] uppercase">
              <Wrench className="w-4 h-4" /> {t.workEyebrow}
            </div>
            <h3 className="mt-3 text-2xl font-medium tracking-tight">{t.workTitle}</h3>
            <p className="mt-4 text-white/70 text-sm leading-relaxed">
              {t.workText}
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
              <span className="text-white/40">{t.relatedLabel}</span>
              <Link to={lp('service', { slug: 'lakcorrectie' })} className="text-[#7aa3ff] hover:text-white transition-colors">{t.linkPolish}</Link>
              <Link to={lp('prijzen')} className="text-[#7aa3ff] hover:text-white transition-colors">{t.linkPricing}</Link>
              <Link to={lp('portfolio')} className="text-[#7aa3ff] hover:text-white transition-colors">{t.linkProjects}</Link>
            </div>
            <div className="mt-6">
              <BookingCTA waMessage={t.workWa} offerteTreatment={t.workTreatment} />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 lg:py-32 border-t border-white/5 bg-white/[0.015]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <SectionLabel><span className="mx-auto">{t.faqLabel}</span></SectionLabel>
            <h2 className="mt-5 text-4xl md:text-5xl font-medium tracking-tight leading-[1.05]">{t.faqTitle}</h2>
          </div>
          <div className="space-y-3">
            {M.faq.map((item, i) => {
              const isOpen = openFaq === i;
              return (
                <div key={i} className={`rounded-xl border overflow-hidden transition-colors ${isOpen ? 'border-[#2f6bff]/40 bg-[#2f6bff]/5' : 'border-white/10 bg-white/[0.02]'}`}>
                  <button onClick={() => setOpenFaq(isOpen ? -1 : i)} className="w-full flex items-center justify-between gap-6 px-6 py-5 text-left">
                    <span className="text-white text-base md:text-lg font-medium">{item.q}</span>
                    <ChevronDown className={`w-5 h-5 text-white/60 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180 text-[#2f6bff]' : ''}`} />
                  </button>
                  {isOpen && <div className="px-6 pb-5 text-white/75 text-sm leading-relaxed">{item.a}</div>}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 lg:py-32 border-t border-white/5">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="p-10 md:p-16 rounded-2xl border border-white/10 bg-gradient-to-br from-[#2f6bff]/12 via-white/[0.03] to-transparent text-center">
            <SectionLabel><span className="mx-auto">{t.finalLabel}</span></SectionLabel>
            <h2 className="mt-5 text-4xl md:text-6xl font-medium tracking-tight leading-[1.05] max-w-3xl mx-auto">
              {t.finalTitle[0]}<br />{t.finalTitle[1]}
            </h2>
            <p className="mt-5 text-white/60 max-w-xl mx-auto">
              {t.finalText}
            </p>
            <div className="mt-10 flex justify-center">
              <BookingCTA size="lg" align="center" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DetailingAanHuis;
